import React, { useEffect, useState } from 'react';

import ContentGroupsList from '../../../components/ContentGroupsList';
import DetailsModal, { Values } from '../../../components/DetailsModal';
import SkeletonStack from '../../../components/SkeletonStack';
import TopicGroupBadge from '../../../components/TopicGroupBadge';
import { useGetTopicGroupsQuery, useNewTopicGroupMutation } from '../../../services/topics';
import { Topic } from '../../../services/topics/types';
import { GroupEntryContainer } from '../index.styles';

const INITIAL_TOPIC_GROUP_FORM = {
  label: '',
  badgeLabel: '',
};

interface TopicsSectionProps {
  subject?: string
}

const TopicsSection = ({ subject }: TopicsSectionProps) => {
  /**
   * Topic Groups Queries & Mutations
   */
  const { data: topicGroups, isFetching } = useGetTopicGroupsQuery({ subject: subject || '' },
    { skip: !subject });

  const [newTopicGroupFormOpen, setNewTopicGroupFormOpen] = useState(false);
  const [newTopicGroupForm, setNewTopicGroupForm] = useState(INITIAL_TOPIC_GROUP_FORM);
  const [newTopicGroup,
    {
      isSuccess: isNewTopicGroupSuccess,
      isLoading: isnewTopicGroupLoading,
      error: newTopicGroupError,
      reset: resetNewTopicGroup,
    }] = useNewTopicGroupMutation();

  /**
   * Topic group functions
   */
  const resetModal = async () => {
    await resetNewTopicGroup();
    setNewTopicGroupForm(INITIAL_TOPIC_GROUP_FORM);
  };

  const onTopicGroupCreate = (values?: Values) => {
    newTopicGroup({
      attributes: {
        subject: subject || '',
        color: '#ffffff',
        ...values,
      },
    });
  };

  // Close modal after topic group is created
  useEffect(() => {
    if (isNewTopicGroupSuccess) {
      setNewTopicGroupFormOpen(false);
      resetModal();
    }
  }, [isNewTopicGroupSuccess]);

  if (subject === undefined) {
    return (
      <SkeletonStack />
    );
  }

  return (
    <>
      <ContentGroupsList
        columns={[
          {
            field: 'label',
            headerName: 'Group',
            width: 480,
            renderCell: ({ row }) => (
              <GroupEntryContainer>
                <TopicGroupBadge
                  color={row?.color}
                  label={row?.badgeLabel}
                />
                {row?.label}
              </GroupEntryContainer>
            ),
          },
        ]}
        data={(topicGroups?.data || [])}
        loading={isFetching}
        actions={{
          create: {
            fn: () => setNewTopicGroupFormOpen(true),
            label: 'Add Topic Group',
          },
        }}
        getRowHref={(row: Topic) => `/admin/topics/${subject}/${row?.id}`}
      />
      <DetailsModal
        open={newTopicGroupFormOpen}
        handleClose={() => setNewTopicGroupFormOpen(false)}
        error={newTopicGroupError}
        title="Add New Topic Group"
        fields={{
          label: {
            editable: true,
            type: 'text',
            label: 'Group Name',
          },
          badgeLabel: {
            editable: true,
            type: 'text',
            label: 'Badge Label',
          },
        }}
        values={newTopicGroupForm}
        buttons={[{
          fn: onTopicGroupCreate,
          label: 'Add',
          loading: isnewTopicGroupLoading,
        }]}
      />
    </>
  );
};

export default TopicsSection;
